import {
  forwardRef,
  useEffect,
  useState,
  type ComponentProps,
  type CSSProperties,
  type ReactNode,
} from 'react';

type RNStyle = CSSProperties | CSSProperties[] | undefined;

function flattenStyle(style: RNStyle): CSSProperties | undefined {
  if (!style) {
    return undefined;
  }
  if (Array.isArray(style)) {
    return Object.assign({}, ...style.filter(Boolean));
  }
  return style;
}

/** RN View defaults to a column flex container; plain divs do not. */
function flexContainerClassName(className: string | undefined, extra?: string): string {
  const isRow = className?.includes('flex-row');
  const hasAlignItems =
    className != null && /\b!?items-(start|end|center|baseline|stretch)\b/.test(className);
  return [
    'flex',
    isRow ? 'flex-row' : 'flex-col',
    hasAlignItems ? '' : 'items-stretch',
    'min-h-0',
    extra,
    className,
  ]
    .filter(Boolean)
    .join(' ');
}

type ViewProps = ComponentProps<'div'> & { style?: RNStyle };

export const View = forwardRef<HTMLDivElement, ViewProps>(function View(
  { style, className, ...props },
  ref,
) {
  return (
    <div
      ref={ref}
      className={flexContainerClassName(className)}
      style={flattenStyle(style)}
      {...props}
    />
  );
});

type TextProps = ComponentProps<'span'> & { style?: RNStyle };

export const Text = forwardRef<HTMLSpanElement, TextProps>(function Text(
  { style, className, ...props },
  ref,
) {
  return <span ref={ref} className={className} style={flattenStyle(style)} {...props} />;
});

type PressableProps = Omit<ComponentProps<'button'>, 'onClick'> & {
  onPress?: () => void;
  disabled?: boolean;
  style?: RNStyle;
};

export function Pressable({ onPress, disabled, style, className, children, ...props }: PressableProps) {
  return (
    <button
      type="button"
      className={flexContainerClassName(className)}
      disabled={disabled}
      style={flattenStyle(style)}
      onClick={() => onPress?.()}
      {...props}
    >
      {children}
    </button>
  );
}

type ScrollViewProps = ComponentProps<'div'> & {
  contentContainerClassName?: string;
  showsVerticalScrollIndicator?: boolean;
  showsHorizontalScrollIndicator?: boolean;
  horizontal?: boolean;
  style?: RNStyle;
};

export function ScrollView({
  contentContainerClassName,
  className,
  style,
  children,
  ...props
}: ScrollViewProps) {
  // Strip RN-only props so they don't leak onto the DOM element.
  const domProps: Record<string, unknown> = { ...props };
  delete domProps.showsVerticalScrollIndicator;
  delete domProps.showsHorizontalScrollIndicator;
  delete domProps.horizontal;

  return (
    <div
      className={flexContainerClassName(className, 'min-h-0 overflow-auto')}
      style={flattenStyle(style)}
      {...domProps}
    >
      {/* Content-sized wrapper — flex-grow here squashes children into the viewport */}
      <div className={flexContainerClassName(contentContainerClassName, 'w-full shrink-0')}>
        {children}
      </div>
    </div>
  );
}

type ActivityIndicatorProps = {
  size?: 'small' | 'large';
  color?: string;
  className?: string;
};

export function ActivityIndicator({ size = 'small', color, className }: ActivityIndicatorProps) {
  const dim = size === 'large' ? 'h-8 w-8 border-2' : 'h-5 w-5 border';
  return (
    <div
      role="status"
      aria-label="Loading"
      className={`inline-block animate-spin rounded-full border-border-strong border-t-accent ${dim} ${className ?? ''}`}
      style={color ? { borderTopColor: color } : undefined}
    />
  );
}

type TextInputProps = Omit<ComponentProps<'input'>, 'onChange'> & {
  onChangeText?: (text: string) => void;
  onSubmitEditing?: () => void;
  keyboardType?: string;
  autoCapitalize?: string;
  selectTextOnFocus?: boolean;
  style?: RNStyle;
};

export function TextInput({
  onChangeText,
  onSubmitEditing,
  selectTextOnFocus,
  value,
  className,
  style,
  onFocus,
  ...props
}: TextInputProps) {
  return (
    <input
      className={className}
      style={flattenStyle(style)}
      value={value ?? ''}
      onChange={(e) => onChangeText?.(e.target.value)}
      onFocus={(e) => {
        if (selectTextOnFocus) {
          e.currentTarget.select();
        }
        onFocus?.(e);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          onSubmitEditing?.();
        }
      }}
      {...props}
    />
  );
}

export const Platform = { OS: 'web' as const, select<T>(spec: { web?: T; default?: T }): T | undefined {
  return spec.web ?? spec.default;
} };

export function useWindowDimensions(): { width: number; height: number } {
  const [dims, setDims] = useState(() => ({
    width: typeof window !== 'undefined' ? window.innerWidth : 1024,
    height: typeof window !== 'undefined' ? window.innerHeight : 768,
  }));

  useEffect(() => {
    const onResize = () => {
      setDims({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return dims;
}

export const StyleSheet = {
  create<T extends Record<string, CSSProperties>>(styles: T): T {
    return styles;
  },
};

export function SafeAreaProvider({ children }: { children: ReactNode }) {
  return <>{children}</>;
}

export function useColorScheme(): 'light' | 'dark' | null {
  if (typeof window === 'undefined') {
    return 'light';
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}
